import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const faqs = [
  {
    question: 'What is the cost of LED sign board in Ahmedabad?',
    answer: 'The cost of LED sign boards in Ahmedabad varies based on size, type, and design complexity. Basic LED sign boards start from ₹5,000, while premium custom designs with 3D elements can range from ₹15,000 to ₹1,00,000+. We offer free quotes tailored to your specific requirements. Contact us for a detailed estimate.',
  },
  {
    question: 'Which is the best signage company in Gujarat?',
    answer: 'ASCENT INTERNATIONAL INC. is recognized as one of the leading signage companies in Gujarat with a 5-star Google rating. With over 15 years of experience, 500+ completed projects, and trusted by brands like Nike, Skechers, and major builders like Shridhar Group and Shivalik Group, we deliver premium quality signage solutions across Ahmedabad and Gujarat.',
  },
  {
    question: 'Do you provide custom acrylic signage?',
    answer: 'Yes! We specialize in custom acrylic signage including 3D letter boards, illuminated acrylic signs, laser-cut acrylic displays, and premium acrylic name plates. Our design team works closely with you to create unique signage that perfectly represents your brand identity.',
  },
  {
    question: 'How long does signage installation take?',
    answer: 'Installation timelines depend on the project scope. Simple signage can be installed within 1-2 days, while larger commercial projects may take 1-2 weeks. We ensure minimal disruption to your business operations and coordinate installation at your convenience. Rush orders are available for urgent requirements.',
  },
  {
    question: 'Do you handle government or large projects?',
    answer: 'Absolutely! We have extensive experience handling government contracts and large-scale commercial projects. Our portfolio includes signage work for government institutions, major real estate developers, shopping complexes, and corporate campuses. We are equipped with the infrastructure and expertise to manage projects of any scale.',
  },
  {
    question: 'What areas in Gujarat do you serve?',
    answer: 'We serve all major areas in Gujarat including Ahmedabad (SG Highway, Satellite, Bopal, Vastrapur, Navrangpura), Gandhinagar, Vadodara, Surat, Rajkot, and other cities. Our installation team covers the entire Gujarat region with prompt service.',
  },
  {
    question: 'Do you offer maintenance services for signage?',
    answer: 'Yes, we provide comprehensive maintenance and repair services for all types of signage. This includes LED replacement, cleaning, electrical repairs, and structural maintenance. We offer annual maintenance contracts (AMC) for businesses that require regular upkeep of their signage.',
  },
  {
    question: 'What types of signage materials do you use?',
    answer: 'We use premium quality materials including high-grade acrylics, stainless steel, aluminum composite panels (ACP), brass, LED modules, and weather-resistant paints. All our materials are sourced from trusted suppliers and come with quality assurance for durability and longevity.',
  },
];

const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="faq"
      className="py-24 md:py-32 bg-slate-50 relative overflow-hidden"
      data-testid="faq-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-sky-100 rounded-full blur-3xl opacity-30"></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
            FAQ
          </span>
          <h2 className="section-heading mb-6">
            FREQUENTLY ASKED <span className="gradient-text">QUESTIONS</span>
          </h2>
          <p className="section-subheading mx-auto">
            Find answers to common questions about our signage services in Ahmedabad and Gujarat.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="faq-item px-6 border-0"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-slate-800 font-['Oswald'] text-lg hover:text-sky-500 hover:no-underline py-5 [&[data-state=open]]:text-sky-500">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-500 font-['Manrope'] text-sm leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <p className="text-slate-500 font-['Manrope'] mb-4">
            Still have questions? We're here to help!
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-block btn-primary"
            data-testid="faq-contact-btn"
          >
            Contact Us
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
